import { useQuery } from "@tanstack/react-query";
import { useRoute } from "wouter";
import type { Account, ClientGroup, Contact, Deal } from "@shared/schema";
import { ContextCard, DispositionPill, LadderPill, VerificationPill } from "@/components/v2/v2-kit";
import { ThemeToggle } from "@/components/v2/theme-toggle";

const sidebarSections = [
  { title: "Needs you", body: "No connected tasks in this foundation slice." },
  { title: "Outstanding inputs", body: "Input status is not connected in this foundation slice." },
  { title: "Agent findings", body: "Agent findings are not connected in this foundation slice." },
  { title: "Freshness", body: "Freshness status is not connected in this foundation slice." },
];

export default function ClientGroupWorkbenchV2() {
  const [, params] = useRoute("/client-groups/:id");
  const id = params?.id;
  const { data: group, isLoading } = useQuery<ClientGroup>({ queryKey: ["/api/crm/client-groups", id], enabled: !!id });
  const { data: contacts = [] } = useQuery<Contact[]>({ queryKey: ["/api/crm/contacts", { clientGroupId: id }], enabled: !!id });
  const { data: accounts = [] } = useQuery<Account[]>({ queryKey: ["/api/crm/accounts", { clientGroupId: id }], enabled: !!id });
  const { data: deals = [] } = useQuery<Deal[]>({ queryKey: ["/api/crm/deals", { clientGroupId: id }], enabled: !!id });

  if (isLoading) return <main className="crmx-v2 crmx-v2-state">Loading client group</main>;
  if (!group) return <main className="crmx-v2 crmx-v2-state">Client group not found</main>;

  return (
    <main className="crmx-v2" data-testid="crmx-v2-client-group-workbench">
      <header className="crmx-v2-header">
        <div>
          <span className="crmx-v2-kicker">Client group workbench</span>
          <h1>{group.groupName}</h1>
          <p>Build progress · figures use verified source records</p>
        </div>
        <div className="crmx-v2-header-actions">
          <span className="crmx-agent-status"><i aria-hidden="true" />Agent: status unavailable</span>
          <ThemeToggle />
        </div>
      </header>

      <ContextCard kicker="V2 kit preview" title="Foundation slice ready for attended review">
        Live trust, task and freshness signals are not connected in this slice.
      </ContextCard>

      <div className="crmx-v2-grid">
        <section className="crmx-v2-content">
          <div className="crmx-v2-summary">
            <article><span>Contacts</span><strong>{contacts.length}</strong><LadderPill state={contacts.length ? "complete" : "empty"} /></article>
            <article><span>Entities</span><strong>{accounts.length}</strong><LadderPill state={accounts.length ? "partial" : "stub"} /></article>
            <article><span>Deals</span><strong>{deals.length}</strong><DispositionPill state={deals.length ? "queued" : "held"} /></article>
          </div>

          <section className="crmx-v2-panel">
            <div className="crmx-v2-panel-heading">
              <div><span className="crmx-v2-kicker">Trust status</span><h2>Can I trust the group record?</h2></div>
              <VerificationPill state="provisional" />
            </div>
            <div className="crmx-v2-table" role="table" aria-label="Group record coverage">
              <div role="row"><span role="cell">Identity and parties</span><LadderPill state="stub" /><VerificationPill state="pending" /></div>
              <div role="row"><span role="cell">Financial position</span><LadderPill state="stub" /><VerificationPill state="pending" /></div>
              <div role="row"><span role="cell">Lending position</span><LadderPill state="stub" /><VerificationPill state="pending" /></div>
            </div>
          </section>
        </section>

        <aside className="crmx-v2-sidebar" aria-label="Group work queue">
          {sidebarSections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </section>
          ))}
        </aside>
      </div>
    </main>
  );
}
